/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / SETTINGS-MAIL-BUILDER — „Eigene Mail" anlegen/bearbeiten (S226)
   =============================================================================
   Builder-Modal für custom_mail_rules (CUT C). Öffnet über „+ Neue Mail" in der
   Vorlagen-Übersicht bzw. „Bearbeiten" an einer Eigene-Mails-Zeile.

   Felder: Name · Trigger (gruppiertes Dropdown, KASKADIERT → Chips/Zeitversatz/
   Empfänger-Regeln aus mail-triggers.js) · Zeitversatz (nur zeitverankert) ·
   Empfänger (Gast/feste Adresse) · Betreff/Body mit Platzhalter-Chips · Anhang
   (keiner/Beleg-PDF/Datei-Upload) · Live-Vorschau MIT fixem, nicht editierbarem
   Abmelde-Footer-Block („gesetzlich vorgeschrieben", nur bei Empfänger=Gast).

   create-on-accept-only (gg-create-on-accept-only): DB-Write ERST beim finalen
   Speichern; Abbrechen hinterlässt nichts. Datei-Upload (static) läuft ebenfalls
   erst beim Speichern → kein verwaister Bucket-Blob bei Abbruch.
============================================================================ */

import { htmlEscape, showToast, fmtEUR } from '../../utils/helpers.js';
import { createDarkOverlay } from '../ui/overlay.js';
import { wrapBodyWithBrandHtml } from '../../core/branding.js';
import {
  triggerGroupsForDropdown, getTrigger, tokensForTrigger, isTriggerTimeAnchored,
} from '../../core/mail-triggers.js';
import {
  createCustomMailRule, updateCustomMailRule, deleteCustomMailRule, getCustomMailRule,
  currentRestaurantId,
} from '../../core/mail-rules-data.js';

const OVERLAY_ID = 'mail-builder-overlay';
const STATIC_MAX_BYTES = 1500000;   // ~1.5 MB (Parität zum Edge-Guard)

// Arbeitskopie der Regel im Modal + Merker für ein neu gewähltes Upload-File
// (wird erst beim Speichern hochgeladen — create-on-accept-only).
let _draft = null;
let _editId = null;
let _pendingFile = null;
let _lastField = 'body';

function _state() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
    || { settings: {}, menu: [] };
}
function supa() { return (window.App && window.App.supabase) || null; }

/** Leerer Draft für eine neue Regel. */
function _blankDraft() {
  return {
    name: '', trigger_event: 'order_confirmed', offset_minutes: 0,
    recipient_mode: 'customer', fixed_email: '',
    subject: '', body: '', attachment: null, enabled: true,
  };
}

// -----------------------------------------------------------------------------
// Pure Helpers (Vitest)
// -----------------------------------------------------------------------------

/** Validiert einen Builder-Draft. → { ok, error }. */
export function validateBuilderRule(d) {
  if (!d || !String(d.name || '').trim()) return { ok: false, error: 'Bitte gib der Mail einen Namen.' };
  if (!getTrigger(d.trigger_event)) return { ok: false, error: 'Bitte wähle einen gültigen Auslöser.' };
  if (d.recipient_mode !== 'customer' && d.recipient_mode !== 'fixed') return { ok: false, error: 'Bitte wähle einen Empfänger.' };
  if (d.recipient_mode === 'fixed') {
    const m = String(d.fixed_email || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(m)) return { ok: false, error: 'Bitte gib eine gültige feste E-Mail-Adresse ein.' };
  }
  if (!String(d.subject || '').trim()) return { ok: false, error: 'Bitte gib einen Betreff ein.' };
  if (!String(d.body || '').trim()) return { ok: false, error: 'Bitte gib einen Text ein.' };
  if (!isTriggerTimeAnchored(d.trigger_event)) {
    // Zeitversatz nur bei zeitverankerten Triggern sinnvoll → hart auf 0.
    d.offset_minutes = 0;
  }
  return { ok: true, error: null };
}

/** Neutrale Vorschau-Fixture (echtes Menü für {{items}}, sonst neutrale Muster). */
export function buildRuleFixture(menu) {
  const list = Array.isArray(menu) ? menu.filter(m => m && m.available !== false) : [];
  const sorted = list.slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  let itemsBlock = '(kein Menü hinterlegt)';
  let totalCents = 2490;
  if (sorted.length) {
    const picks = sorted.slice(0, 2);
    const qtys = [2, 1];
    itemsBlock = picks.map((it, i) => `• ${qtys[i] || 1}x ${it.name || 'Position'}`).join('\n');
    totalCents = picks.reduce((s, it, i) => s + (Number(it.priceCents || it.price_cents || 0) * (qtys[i] || 1)), 0) || 2490;
  }
  return {
    name: 'Gast', order_number: 'B-1234', guests: '2',
    pickup_date: 'Fr., 10. Juli 2026', pickup_time: '18:30',
    reservation_date: 'Fr., 10. Juli 2026', reservation_time: '19:00',
    total: fmtEUR(totalCents / 100).replace('€', 'EUR').trim(),
    items: itemsBlock, items_block: itemsBlock,
    brand: (_state().settings && _state().settings.restaurantName) || 'unser Restaurant',
  };
}

/** Ersetzt {{token}}/{token} im Template durch Fixture-Werte (Vorschau). */
export function fillRuleTemplate(tpl, fixture) {
  let out = String(tpl == null ? '' : tpl).replace(/\\n/g, '\n');
  for (const [k, v] of Object.entries(fixture || {})) {
    out = out.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'g'), v == null ? '' : String(v))
             .replace(new RegExp('\\{\\s*' + k + '\\s*\\}', 'g'), v == null ? '' : String(v));
  }
  return out;
}

// -----------------------------------------------------------------------------
// Modal
// -----------------------------------------------------------------------------

/** Öffnet den Builder. ruleId leer = neu; sonst Bearbeiten. */
export function openMailBuilderModal(ruleId) {
  closeMailBuilderModal();
  _editId = ruleId || null;
  _pendingFile = null;
  _lastField = 'body';
  if (_editId) {
    const r = getCustomMailRule(_editId);
    if (!r) { showToast('Regel nicht gefunden.', 'error'); return; }
    _draft = {
      name: r.name, trigger_event: r.trigger_event, offset_minutes: r.offset_minutes || 0,
      recipient_mode: r.recipient_mode, fixed_email: r.fixed_email || '',
      subject: r.subject, body: r.body, attachment: r.attachment || null, enabled: r.enabled !== false,
    };
  } else {
    _draft = _blankDraft();
  }
  const overlay = createDarkOverlay({ id: OVERLAY_ID, zIndex: 9000, onClose: closeMailBuilderModal });
  overlay.innerHTML = `
    <div id="mail-builder-panel" role="dialog" aria-modal="true"
         style="background:var(--bg-elev,#15201b);color:var(--ink,#e8efe8);width:min(760px,100%);max-height:92vh;
                display:flex;flex-direction:column;border-radius:12px;box-shadow:0 12px 40px rgba(0,0,0,.5);overflow:hidden;"></div>`;
  document.body.appendChild(overlay);
  _paintBuilder();
}

export function closeMailBuilderModal() {
  const el = document.getElementById(OVERLAY_ID);
  if (el) {
    if (typeof el._ggDetachKey === 'function') el._ggDetachKey();
    if (el.parentNode) el.parentNode.removeChild(el);
  }
  _draft = null; _editId = null; _pendingFile = null;
}

/** Liest die aktuellen Feldwerte aus dem DOM in den Draft (vor Re-Render/Save). */
function _syncDraftFromDom() {
  if (!_draft) return;
  const g = (id) => document.getElementById(id);
  if (g('mb-name')) _draft.name = g('mb-name').value;
  if (g('mb-trigger')) _draft.trigger_event = g('mb-trigger').value;
  if (g('mb-offset')) _draft.offset_minutes = parseInt(g('mb-offset').value, 10) || 0;
  if (g('mb-recipient')) _draft.recipient_mode = g('mb-recipient').value;
  if (g('mb-fixed-email')) _draft.fixed_email = g('mb-fixed-email').value;
  if (g('mb-subject')) _draft.subject = g('mb-subject').value;
  if (g('mb-body')) _draft.body = g('mb-body').value;
  if (g('mb-attachment')) {
    const v = g('mb-attachment').value;
    if (v === 'order_receipt') _draft.attachment = { type: 'order_receipt' };
    else if (v === 'static') _draft.attachment = _draft.attachment && _draft.attachment.type === 'static' ? _draft.attachment : { type: 'static' };
    else _draft.attachment = null;
  }
}

function _paintBuilder() {
  const panel = document.getElementById('mail-builder-panel');
  if (!panel || !_draft) return;
  panel.innerHTML = _renderBuilderInner();
  _mountBuilderPreview();
}

function _renderBuilderInner() {
  const d = _draft;
  const trig = getTrigger(d.trigger_event);
  const timeAnchored = isTriggerTimeAnchored(d.trigger_event);
  const tokens = tokensForTrigger(d.trigger_event);
  const groups = triggerGroupsForDropdown();

  const triggerOpts = groups.map(g => `
    <optgroup label="${g.icon} ${htmlEscape(g.label)}">
      ${g.options.map(o => `<option value="${o.id}" ${o.id === d.trigger_event ? 'selected' : ''}>${htmlEscape(o.label)}</option>`).join('')}
    </optgroup>`).join('');

  const chips = tokens.map(t =>
    `<button type="button" onclick="mailBuilderInsertToken('${htmlEscape(t)}')"
       style="margin:0 6px 6px 0;cursor:pointer;border:1px solid var(--border,#2a352e);background:rgba(255,255,255,.06);color:var(--ink,#e8efe8);font-size:.8rem;padding:3px 9px;border-radius:6px;font-family:ui-monospace,Menlo,monospace;">${htmlEscape(t)}</button>`
  ).join('');

  const attType = d.attachment && d.attachment.type;
  const staticInfo = (attType === 'static' && d.attachment && d.attachment.filename)
    ? `<div style="color:var(--ink-muted,#8b9890);font-size:.78rem;margin-top:4px;">Datei: ${htmlEscape(d.attachment.filename)}</div>`
    : '';

  return `
    <div style="padding:16px 20px;border-bottom:1px solid var(--border,#2a352e);display:flex;justify-content:space-between;align-items:center;gap:12px;">
      <strong style="font-size:1.05rem;">${_editId ? 'Eigene Mail bearbeiten' : 'Neue eigene Mail'}</strong>
      <button onclick="closeMailBuilderModal()" aria-label="Schließen"
              style="cursor:pointer;border:0;background:transparent;color:var(--ink-soft,#b3c0b6);font-size:1.15rem;padding:4px 8px;">✕</button>
    </div>
    <div style="flex:1;min-height:0;overflow-y:auto;padding:18px 20px;background:var(--bg-elev,#15201b);">
      <div class="field" style="margin-bottom:12px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Name (intern)</label>
        <input type="text" id="mb-name" value="${htmlEscape(d.name)}" placeholder="z. B. Dankeschön nach Abholung" style="width:100%;">
      </div>
      <div class="field" style="margin-bottom:12px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Auslöser</label>
        <select id="mb-trigger" onchange="mailBuilderOnTriggerChange()" style="width:100%;">${triggerOpts}</select>
      </div>
      <div class="field" style="margin-bottom:12px;${timeAnchored ? '' : 'display:none;'}" id="mb-offset-field">
        <label style="color:var(--ink-soft,#b3c0b6);">Zeitversatz zum Termin (Minuten — negativ = vorher, positiv = nachher)</label>
        <input type="number" id="mb-offset" value="${Number(d.offset_minutes) || 0}" step="15" style="width:100%;">
      </div>
      <div class="field" style="margin-bottom:12px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Empfänger</label>
        <select id="mb-recipient" onchange="mailBuilderOnRecipientChange()" style="width:100%;">
          <option value="customer" ${d.recipient_mode === 'customer' ? 'selected' : ''}>Gast (nur bestätigte Newsletter-Abonnenten)</option>
          <option value="fixed" ${d.recipient_mode === 'fixed' ? 'selected' : ''}>Feste Adresse (intern)</option>
        </select>
      </div>
      <div class="field" style="margin-bottom:12px;${d.recipient_mode === 'fixed' ? '' : 'display:none;'}" id="mb-fixed-field">
        <label style="color:var(--ink-soft,#b3c0b6);">Feste E-Mail-Adresse</label>
        <input type="email" id="mb-fixed-email" value="${htmlEscape(d.fixed_email || '')}" placeholder="team@restaurant.de" style="width:100%;">
      </div>
      <div class="field" style="margin-bottom:12px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Betreff</label>
        <input type="text" id="mb-subject" value="${htmlEscape(d.subject)}" onfocus="mailBuilderTrackField('subject')" style="width:100%;">
      </div>
      <div class="field" style="margin-bottom:8px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Text</label>
        <textarea id="mb-body" rows="10" onfocus="mailBuilderTrackField('body')" style="width:100%;font-family:ui-monospace,Menlo,monospace;font-size:.88rem;">${htmlEscape(d.body)}</textarea>
      </div>
      <div style="margin:0 0 14px;">
        <div style="color:var(--ink-muted,#8b9890);font-size:.78rem;margin-bottom:6px;">Platzhalter einfügen:</div>
        ${chips || '<span style="color:var(--ink-muted,#8b9890);font-size:.8rem;">—</span>'}
      </div>
      <div class="field" style="margin-bottom:12px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Anhang</label>
        <select id="mb-attachment" onchange="mailBuilderOnAttachmentChange()" style="width:100%;">
          <option value="none" ${!attType ? 'selected' : ''}>Kein Anhang</option>
          <option value="order_receipt" ${attType === 'order_receipt' ? 'selected' : ''}>Beleg-PDF der Bestellung</option>
          <option value="static" ${attType === 'static' ? 'selected' : ''}>Datei-Upload (fest)</option>
        </select>
        <div id="mb-static-field" style="margin-top:8px;${attType === 'static' ? '' : 'display:none;'}">
          <input type="file" id="mb-static-file" onchange="mailBuilderOnFilePicked(event)" style="width:100%;">
          ${staticInfo}
          <div style="color:var(--ink-muted,#8b9890);font-size:.72rem;margin-top:4px;">max. 1,5 MB</div>
        </div>
      </div>
      <div style="border-top:1px solid var(--border,#2a352e);padding-top:12px;">
        <div style="color:var(--ink-soft,#b3c0b6);font-size:.82rem;margin-bottom:8px;">Vorschau (Testdaten)${d.recipient_mode === 'customer' ? ' — inkl. gesetzlich vorgeschriebenem Abmelde-Footer' : ''}:</div>
        <div id="mb-preview-host" style="width:100%;height:40vh;background:#efe9dd;border-radius:8px;overflow:hidden;"></div>
      </div>
    </div>
    <div style="padding:14px 20px;border-top:1px solid var(--border,#2a352e);display:flex;gap:10px;justify-content:space-between;align-items:center;">
      <div>${_editId ? `<button type="button" class="btn btn-danger btn-sm" onclick="mailBuilderDelete()">Löschen</button>` : ''}</div>
      <div style="display:flex;gap:10px;">
        <button type="button" class="btn btn-ghost" onclick="closeMailBuilderModal()">Abbrechen</button>
        <button type="button" class="btn btn-secondary btn-sm" onclick="mailBuilderRefreshPreview()">Vorschau</button>
        <button type="button" class="btn btn-primary" onclick="mailBuilderSave()">${_editId ? 'Speichern' : 'Anlegen'}</button>
      </div>
    </div>`;
}

function _mountBuilderPreview() {
  const host = document.getElementById('mb-preview-host');
  if (!host || !_draft) return;
  const d = _draft;
  const fixture = buildRuleFixture(_state().menu || []);
  const bodyFilled = fillRuleTemplate(d.body, fixture);
  const restaurantName = (_state().settings && _state().settings.restaurantName) || '';
  const opts = { restaurantName };
  // Bei Empfänger=Gast: fixer Abmelde-Footer-Block in der Vorschau (Placeholder-Link).
  if (d.recipient_mode === 'customer') opts.unsubscribeLink = '#abmelden';
  host.innerHTML = '';
  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', '');
  frame.setAttribute('title', 'Mail-Vorschau');
  frame.style.cssText = 'width:100%;height:100%;border:0;background:#efe9dd;';
  host.appendChild(frame);
  Promise.resolve()
    .then(() => wrapBodyWithBrandHtml(bodyFilled, opts))
    .then((html) => { if (frame.isConnected) frame.srcdoc = html; })
    .catch(() => { if (frame.isConnected) frame.srcdoc = '<p style="font-family:sans-serif;padding:16px;">Vorschau nicht verfügbar.</p>'; });
}

// ---- Inline-Handler ---------------------------------------------------------

export function mailBuilderOnTriggerChange() {
  _syncDraftFromDom();
  _paintBuilder();   // Kaskade: Chips/Zeitversatz-Feld/Empfänger-Hinweis neu
}
export function mailBuilderOnRecipientChange() {
  _syncDraftFromDom();
  _paintBuilder();
}
export function mailBuilderOnAttachmentChange() {
  _syncDraftFromDom();
  _paintBuilder();
}
export function mailBuilderTrackField(f) { if (f === 'subject' || f === 'body') _lastField = f; }
export function mailBuilderInsertToken(token) {
  const id = _lastField === 'subject' ? 'mb-subject' : 'mb-body';
  const el = document.getElementById(id);
  if (!el) return;
  const s = el.selectionStart != null ? el.selectionStart : el.value.length;
  const e = el.selectionEnd != null ? el.selectionEnd : el.value.length;
  el.value = el.value.slice(0, s) + token + el.value.slice(e);
  const caret = s + token.length;
  el.focus();
  try { el.setSelectionRange(caret, caret); } catch (_) { /* ignore */ }
  _syncDraftFromDom();
}
export function mailBuilderRefreshPreview() { _syncDraftFromDom(); _mountBuilderPreview(); }

export function mailBuilderOnFilePicked(ev) {
  const file = ev && ev.target && ev.target.files && ev.target.files[0];
  if (!file) return;
  if (file.size > STATIC_MAX_BYTES) { showToast('Die Datei ist größer als 1,5 MB.', 'error'); ev.target.value = ''; return; }
  _pendingFile = file;
  _draft.attachment = { type: 'static', filename: file.name, byte_size: file.size, content_type: file.type || 'application/octet-stream' };
}

/** Lädt eine gewählte Datei erst beim Speichern hoch (create-on-accept-only). */
async function _uploadPendingFileIfAny() {
  if (!_pendingFile) return;
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client für den Upload.');
  const rid = currentRestaurantId() || 'rest';
  const safe = String(_pendingFile.name || 'anhang').replace(/[^\w.\-]+/g, '_');
  const path = `${rid}/${Date.now()}_${safe}`;
  const { error } = await client.storage.from('mail-attachments').upload(path, _pendingFile, {
    contentType: _pendingFile.type || 'application/octet-stream', upsert: false,
  });
  if (error) throw error;
  _draft.attachment = {
    type: 'static', path, filename: _pendingFile.name,
    byte_size: _pendingFile.size, content_type: _pendingFile.type || 'application/octet-stream',
  };
  _pendingFile = null;
}

export async function mailBuilderSave() {
  _syncDraftFromDom();
  const v = validateBuilderRule(_draft);
  if (!v.ok) { showToast(v.error, 'error'); return; }
  // Static-Anhang gewählt aber keine Datei hochgeladen → blocken.
  if (_draft.attachment && _draft.attachment.type === 'static' && !_pendingFile && !_draft.attachment.path) {
    showToast('Bitte eine Datei für den Anhang auswählen.', 'error'); return;
  }
  try {
    await _uploadPendingFileIfAny();
    if (_editId) await updateCustomMailRule(_editId, _draft);
    else await createCustomMailRule(_draft);
    showToast(_editId ? 'Mail gespeichert.' : 'Mail angelegt.', 'success');
    closeMailBuilderModal();
    if (typeof window.renderSettings === 'function') window.renderSettings();
  } catch (e) {
    console.error('[mailBuilderSave]', e);
    showToast('Speichern fehlgeschlagen: ' + (e && e.message ? e.message : e), 'error');
  }
}

export async function mailBuilderDelete() {
  if (!_editId) return;
  const doDelete = async () => {
    try {
      await deleteCustomMailRule(_editId);
      showToast('Mail gelöscht.', 'success');
      closeMailBuilderModal();
      if (typeof window.renderSettings === 'function') window.renderSettings();
    } catch (e) {
      console.error('[mailBuilderDelete]', e);
      showToast('Löschen fehlgeschlagen.', 'error');
    }
  };
  if (typeof window.styledConfirm === 'function') {
    const ok = await window.styledConfirm('Diese eigene Mail wirklich löschen?', { danger: true });
    if (ok) doDelete();
  } else {
    doDelete();
  }
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    openMailBuilderModal, closeMailBuilderModal,
    mailBuilderOnTriggerChange, mailBuilderOnRecipientChange, mailBuilderOnAttachmentChange,
    mailBuilderTrackField, mailBuilderInsertToken, mailBuilderRefreshPreview,
    mailBuilderOnFilePicked, mailBuilderSave, mailBuilderDelete,
  });
}
